import {isCopilot} from '@github-ui/assignees/copilot-user'
import {serializeDraftIssueProjects} from '@github-ui/copilot-chat-helpers/draft-issue-serialization'
import type {DraftIssueProjectReference} from '@github-ui/copilot-chat-types'
import type {IssueCreatePayload} from '@github-ui/issue-create/Model'
import {instanceOfIssueFormData} from '@github-ui/issue-create/Model'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import type {IssueFieldWithUnsavedValue} from '@github-ui/issue-metadata-types/issue-field-with-unsaved-value'
import {fetchRepository, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {useMutation, useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {RefObject} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {Environment} from 'relay-runtime'

import type {DraftIssue, DraftOrExistingIssue} from '../content-preview-types'
import {fetchRepositoryDefaultBranch} from '../utils/use-repository-default-branch'
import {type TreeNode, visitDependenciesAsync} from './use-draft-issue-tree-map'
import type {ContinuableError} from './use-on-save-callback'
import {useTransformCopilotImageURLsFunction} from './use-transform-copilot-image-urls-function'

type IssueFieldPayload = {
  field_id: string
  text_value?: string
  single_select_option_id?: string
  date_value?: string
  number_value?: number
}

type DraftIssueMetadata = {
  title: string
  tag: string
  repository_nwo: string
  body?: string
  parent_tag?: string
  labels?: string[]
  assignees?: string[]
  milestone?: string
  template?: string
  issue_type?: string
  projects?: Array<DraftIssueProjectReference | string>
  blocked_by_tags?: string[]
  agent_assignment?: AgentAssignmentAttributes
  issue_fields?: IssueFieldPayload[]
}
type ExistingIssueMetadata = {
  tag: string
  parent_tag?: string
  repository_nwo: string
  number: number
  blocked_by_tags?: string[]
  agent_assignment?: AgentAssignmentAttributes
}

type AgentAssignmentAttributes = {
  target_repo?: string
  base_ref?: string
  custom_instructions?: string
  custom_agent?: string
  model?: string
}

type IssueMetadata = DraftIssueMetadata | ExistingIssueMetadata

// serialize issue fields into the payload format expected by the API
// returns undefined if there are no valid fields
function serializeIssueFields(issueFields: IssueFieldWithUnsavedValue[] | undefined): IssueFieldPayload[] | undefined {
  if (!issueFields || issueFields.length === 0) return undefined

  const serialized = issueFields
    .map(field => {
      if (!field.field.dataType || !field.field.id) return null

      const payload: IssueFieldPayload = {
        // eslint-disable-next-line camelcase
        field_id: field.field.id,
      }

      switch (field.field.dataType) {
        case 'TEXT':
          if (field.value !== undefined) {
            // eslint-disable-next-line camelcase
            payload.text_value = field.value as string
          }
          break
        case 'SINGLE_SELECT': {
          const option = field.value as {id: string} | null
          if (option?.id) {
            // eslint-disable-next-line camelcase
            payload.single_select_option_id = option.id
          }
          break
        }
        case 'DATE': {
          const dateVal = field.value as Date | string | null
          if (dateVal) {
            // eslint-disable-next-line camelcase
            payload.date_value = dateVal instanceof Date ? dateVal.toISOString() : String(dateVal)
          }
          break
        }
        case 'NUMBER': {
          const numVal = field.value as number | null
          if (numVal != null) {
            // eslint-disable-next-line camelcase
            payload.number_value = numVal
          }
          break
        }
        default:
          return null
      }

      // Drop fields that have no value set
      if (Object.keys(payload).length === 1) return null

      return payload
    })
    .filter((v): v is IssueFieldPayload => v !== null)

  return serialized.length > 0 ? serialized : undefined
}

type BulkCreateResponse = {
  jobStatusUrl: string
  jobId: number
}

type IssueIdentifiers = {
  tag: string
  id: number
  number: number
  href: string
  repository_id: number
  errors?: ContinuableError[]
}

export type BulkCreateStatusResponse = {
  job: {
    state: 'pending' | 'queued' | 'started' | 'success' | 'error'
    error_message: string | null
    percentage: number
    completed_issues: IssueIdentifiers[]
  }
}

export function useBulkCreateMutationWithStatus({
  issueNode,
  issueFormRef,
  issueTemplate,
}: {
  issueNode: TreeNode<DraftOrExistingIssue> | undefined
  issueFormRef?: RefObject<IssueFormRef | null>
  issueTemplate?: IssueCreatePayload | null
}) {
  const mutation = useBulkCreateMutation({issueNode, issueFormRef, issueTemplate})
  const query = useBulkCreateStatusQuery(mutation.data?.jobStatusUrl)

  return {
    // Caller will invoke the mutation
    mutate: mutation.mutate,
    // But get results from the job status endpoint
    data: query.data,
    // We coalesce the errors
    error: mutation.error || query.error,
    // As well as the status checks
    isError: mutation.isError || query.isError,
    // If either `fetch` is running, _or_ the job is still running
    isLoading:
      mutation.isPending || query.isLoading || ['pending', 'queued', 'started'].includes(query.data?.job.state || ''),
    // If the job completed successfully
    isComplete: query.data?.job.state === 'success',
  }
}

function useBulkCreateMutation({
  issueNode,
  issueFormRef,
  issueTemplate,
}: {
  issueNode: TreeNode<DraftOrExistingIssue> | undefined
  issueFormRef?: RefObject<IssueFormRef | null>
  issueTemplate?: IssueCreatePayload | null
}) {
  const environment = useRelayEnvironment()
  const transformImageUrls = useTransformCopilotImageURLsFunction()
  const isSingleIssue = !!issueNode && !issueNode.parent && issueNode.children.length === 0

  return useMutation({
    mutationKey: ['copilot-issues-bulk-create', issueNode?.item.tag],
    mutationFn: async () => {
      if (!issueNode) {
        throw new Error('Cannot create issue: issue node is not yet available')
      }

      // Flatten the tree into the list expected by the API.
      const issues: IssueMetadata[] = []
      const repositories = new Map<string, RepositoryPickerRepository | null>()
      await visitDependenciesAsync(issueNode, async node => {
        const draft = node.item
        if (draft.type === 'existing-issue') {
          // Existing issues don't need any additional transformation,
          // as we're not editing for content
          issues.push({
            tag: draft.tag,
            // eslint-disable-next-line camelcase
            parent_tag: draft.parentTag,
            // eslint-disable-next-line camelcase
            repository_nwo: draft.repository,
            number: draft.number,
            // Rather than referencing the `blockedByTags` directly from the draft issue, we use the node references
            // to ensure we're covering relationships that Copilot may have returned in either direction.
            // eslint-disable-next-line camelcase
            blocked_by_tags: node.blockedBy.length ? node.blockedBy.map(dependency => dependency.item.tag) : undefined,
          })
          return
        }

        let issueBodyToSave = draft.body

        // For single issues with issue forms, use form markdown instead of draft body.
        // The validation has already performed in CreateIssueButton, so if we get here,
        // we know that the form content is valid.
        if (isSingleIssue && issueTemplate && instanceOfIssueFormData(issueTemplate.data) && issueFormRef?.current) {
          issueBodyToSave = issueFormRef.current.markdown() ?? draft.body
        }

        if (issueBodyToSave && draft.repository) {
          let repository = repositories.get(draft.repository)
          if (!repository) {
            const [repoOwner, repoName] = draft.repository.split('/')
            if (repoOwner != null && repoName != null) {
              repository = await fetchRepository(repoOwner, repoName)
              repositories.set(draft.repository, repository)
            }
          }
          issueBodyToSave = await transformImageUrls(repository, issueBodyToSave)
        }

        issues.push({
          title: draft.name,
          tag: draft.tag,
          // eslint-disable-next-line camelcase, @typescript-eslint/no-non-null-assertion
          repository_nwo: draft.repository!,
          body: issueBodyToSave,
          // eslint-disable-next-line camelcase
          parent_tag: draft.parentTag,
          labels: draft.labels,
          assignees: draft.assignees,
          milestone: draft.milestone,
          // eslint-disable-next-line camelcase
          issue_type: draft.issueType,
          template: issueTemplate?.fileName,
          // Send project objects with both id and title when feature flag is enabled
          // Otherwise send just titles for backward compatibility
          projects: serializeDraftIssueProjects(draft.projects),
          // Rather than referencing the `blockedByTags` directly from the draft issue, we use the node references
          // to ensure we're covering relationships that Copilot may have returned in either direction.
          // eslint-disable-next-line camelcase
          blocked_by_tags: node.blockedBy.length ? node.blockedBy.map(dependency => dependency.item.tag) : undefined,
          // eslint-disable-next-line camelcase
          agent_assignment: await createAgentAssignment(draft, environment),
          // eslint-disable-next-line camelcase
          issue_fields: serializeIssueFields(draft.issueFields),
        })
      })

      const bulkCreateResponse = await reactFetchJSON('/copilot_issues/bulk_create', {
        method: 'POST',
        body: {
          data: {
            issues,
          },
        },
      })

      if (bulkCreateResponse.status === 429) {
        const rateLimitData = (await bulkCreateResponse.json()) as {error: string}
        throw new Error(rateLimitData.error || 'Rate limit exceeded. Please try again later.')
      }

      if (!bulkCreateResponse.ok) {
        const errorData = (await bulkCreateResponse.json()) as {error: {issue_tag: string; message: string}}
        throw new Error(errorData.error.message || `Failed to create issues.`)
      }

      return (await bulkCreateResponse.json()) as BulkCreateResponse
    },
  })
}

export async function createAgentAssignment(
  draft: DraftIssue,
  environment: Environment,
): Promise<AgentAssignmentAttributes | undefined> {
  if (!(draft.assignees?.some(assignee => isCopilot(assignee)) ?? false)) return undefined

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [owner, repo] = draft.repository!.split('/')
  // By the time we've reached this code, the repo must've been validated already, so owner and repo must be defined
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const defaultBranch = await fetchRepositoryDefaultBranch(environment, owner!, repo!)
  if (!defaultBranch) return undefined
  // TODO - we can only get the additional attributes from user's selections in the "Assign to Copilot" modal,
  // and we don't have design to hook this up into our flow yet. So we'll only support Copilot assignment on default
  // branch for now, and leave other attributes as undefined to fallback to their default values.
  // https://github.com/github/copilot-voyager/issues/1036
  return {
    // eslint-disable-next-line camelcase
    target_repo: draft.repository,
    // eslint-disable-next-line camelcase
    base_ref: defaultBranch,
  }
}

function useBulkCreateStatusQuery(jobStatusUrl: string | undefined) {
  return useQuery({
    enabled: !!jobStatusUrl,
    queryKey: ['copilot-issues-bulk-create-status', jobStatusUrl],
    queryFn: async ctx => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const response = await reactFetchJSON(jobStatusUrl!) // query is only enabled if jobStatusUrl exists
      if (!response.ok) {
        const errorData = (await response.json()) as BulkCreateStatusResponse
        // Throwing an error will put the query into an error state.
        // However we still want to update any existing data in the cache,
        // as the job status may have succeeded on a previous iteration.
        ctx.client.setQueryData(ctx.queryKey, errorData)
        throw new Error(errorData?.job.error_message || 'Failed to fetch job status')
      }
      return (await response.json()) as BulkCreateStatusResponse
    },
    refetchInterval: query => {
      // If we threw an error, stop polling
      if (query.state.error) return false

      // If `JobStatus#finished?`, stop polling
      if (query.state.data?.job.state === 'success') return false
      if (query.state.data?.job.state === 'error') return false

      return 2000 // Poll every 2 seconds
    },
  })
}
